import { useState } from 'react';
import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Loader2, RefreshCw, Search, Clock, TrendingUp, ArrowRight } from 'lucide-react';
import { useGetAvailableSwapOffers } from '../hooks/useQueries';
import { SwapOffer } from '../backend';
import { Principal } from '@dfinity/principal';

interface AvailableSwapsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelectOffer: (offer: SwapOffer) => void;
}

export default function AvailableSwapsModal({ open, onOpenChange, onSelectOffer }: AvailableSwapsModalProps) {
    const { data: offers, isLoading, refetch, isFetching } = useGetAvailableSwapOffers();
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<'rate' | 'expiration'>('expiration');

    const formatTimeRemaining = (expiration: bigint) => {
        const now = Date.now() * 1000000; // Convert to nanoseconds
        const expirationNs = Number(expiration);
        const remainingMs = (expirationNs - now) / 1000000;

        if (remainingMs <= 0) return 'Expired';

        const hours = Math.floor(remainingMs / (1000 * 60 * 60));
        const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 24) {
            const days = Math.floor(hours / 24);
            return `${days}d ${hours % 24}h`;
        }
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const truncateAddress = (principal: Principal) => {
        const text = principal.toString();
        return `${text.slice(0, 6)}...${text.slice(-4)}`;
    };

    // Memoize filtered and sorted offers to prevent recalculation on every render
    const filteredOffers = React.useMemo(() => {
        if (!offers) return [];
        if (!searchQuery) return offers;
        const query = searchQuery.toLowerCase();
        return offers.filter(offer =>
            offer.fromToken.toLowerCase().includes(query) ||
            offer.toToken.toLowerCase().includes(query)
        );
    }, [offers, searchQuery]);

    const sortedOffers = React.useMemo(() => {
        return [...filteredOffers].sort((a, b) => {
            if (sortBy === 'rate') {
                return b.rate - a.rate;
            }
            return Number(a.expiration - b.expiration);
        });
    }, [filteredOffers, sortBy]);

    const handleSelectOffer = (offer: SwapOffer) => {
        onSelectOffer(offer);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[80vh]">
                <DialogHeader>
                    <DialogTitle className="text-2xl">Available Swap Offers</DialogTitle>
                    <DialogDescription>
                        Browse active atomic swap offers from other users. Click any offer to prefill the swap interface.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Search and Controls */}
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by token symbol..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => refetch()}
                            disabled={isFetching}
                            title="Refresh offers"
                        >
                            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>

                    {/* Sort Options */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Sort by:</span>
                        <Button
                            variant={sortBy === 'expiration' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSortBy('expiration')}
                        >
                            <Clock className="h-3 w-3 mr-1" />
                            Expiration
                        </Button>
                        <Button
                            variant={sortBy === 'rate' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSortBy('rate')}
                        >
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Rate
                        </Button>
                    </div>

                    {/* Offers List */}
                    <ScrollArea className="h-[400px] pr-4">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : sortedOffers.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <p className="text-muted-foreground mb-2">
                                    {searchQuery ? 'No offers match your search' : 'No active swap offers available'}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Check back later or create your own offer
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {sortedOffers.map((offer) => (
                                    <div
                                        key={offer.id.toString()}
                                        className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors cursor-pointer bg-card"
                                        onClick={() => handleSelectOffer(offer)}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-lg">{offer.fromToken}</span>
                                                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-bold text-lg">{offer.toToken}</span>
                                                </div>
                                            </div>
                                            <Badge variant="outline" className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {formatTimeRemaining(offer.expiration)}
                                            </Badge>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-3">
                                            <div>
                                                <p className="text-xs text-muted-foreground mb-1">Offering</p>
                                                <p className="font-medium">
                                                    {offer.fromAmount.toFixed(8)} {offer.fromToken}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground mb-1">Requesting</p>
                                                <p className="font-medium">
                                                    {offer.toAmount.toFixed(8)} {offer.toToken}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between pt-3 border-t border-border">
                                            <div>
                                                <p className="text-xs text-muted-foreground mb-1">Exchange Rate</p>
                                                <p className="text-sm font-medium">
                                                    1 {offer.fromToken} = {offer.rate.toFixed(8)} {offer.toToken}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-muted-foreground mb-1">Creator</p>
                                                <p className="text-xs font-mono">
                                                    {truncateAddress(offer.creator)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ScrollArea>

                    {/* Footer Info */}
                    {sortedOffers.length > 0 && (
                        <div className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
                            Showing {sortedOffers.length} active {sortedOffers.length === 1 ? 'offer' : 'offers'}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
